import { useState } from 'react';
import qs from 'qs';

export function useSynchronizedQueryStringForSearchForHighends({
  refinementLists,
  ranges,
}) {
  const [searchState, setSearchState] = useState(() =>
    urlToSearchState({ refinementLists, ranges })
  );
  const [debouncedSetState, setDebouncedSetState] = useState(null);

  function onSearchStateChange(newSearchState) {
    clearTimeout(debouncedSetState);

    setDebouncedSetState(
      setTimeout(() => {
        const url = new URL(window.location.href);
        url.search = searchStateToUrl(newSearchState);
        history.replaceState({ path: url.href }, null, url.href);
      }, 400)
    );

    setSearchState(newSearchState);
  }

  return {
    searchState,
    onSearchStateChange,
  };
}

/**
 * Extracts an initial search state from the URL
 * @param refinementLists List of all the keys that represent a ref. list
 * @param ranges List of all the keys that represent a range
 * @returns a searchState that can be fed to InstantSearch
 */
function urlToSearchState({ refinementLists, ranges }) {
  const parsedQs = qs.parse(window.location.search.slice(1));

  const searchState = {
    refinementList: {},
    range: {},
  };

  // Extract the textual query
  if (parsedQs.query) {
    searchState.query = parsedQs.query;
  }

  // Extract the page
  if (parsedQs.page) {
    searchState.page = parsedQs.page;
  }

  // Extract all possible refinement lists from the URL
  refinementLists.forEach((key) => {
    const valuesString = parsedQs[key];

    if (!valuesString) {
      return;
    }

    searchState.refinementList[key] = valuesString.split(',');
  });

  // Extract all possible ranges from the URL
  ranges.forEach((key) => {
    const rangeString = parsedQs[key];

    if (!rangeString) {
      return;
    }

    const [min, max] = rangeString.split(',');

    searchState.range[key] = {
      min: Number(min) || undefined,
      max: Number(max) || undefined,
    };
  });

  return searchState;
}

/**
 * Transform a search state to a query string
 * @param searchState
 * @returns a query string
 */
function searchStateToUrl(searchState) {
  const qsObject = {};

  // Serialize the textual query
  if (searchState.query) {
    qsObject.query = searchState.query;
  }

  // Serialize the page
  if (searchState.page !== 1) {
    qsObject.page = searchState.page;
  }

  // Serialize all refinement lists
  for (const key in searchState.refinementList) {
    const value = searchState.refinementList[key];

    if (value.length === 0) {
      continue;
    }

    qsObject[key] = value.join(',');
  }

  // Serialize all ranges
  for (const key in searchState.range) {
    const { min, max } = searchState.range[key];

    if (min === undefined && max === undefined) {
      continue;
    }

    qsObject[key] = `${min || ''},${max || ''}`;
  }

  // Encode the query string object so that we can append it to a URL
  return qs.stringify(qsObject);
}

export function createURL(searchState) {
  return '?' + searchStateToUrl(searchState);
}
